// The network: contacts and stakeholder organisations, and what they
// contributed to (SPEC-v2 N1-N5; API.md 7.5, endpoints 64-72).
import { z } from "zod";
import { countryCode } from "../countries";
import {
  GENDERS,
  STAKEHOLDER_TYPES,
  type ContactSource,
  type ContributionRole,
  type Gender,
  type RegistrationStatus,
  type StakeholderType,
} from "../enums";
import { LIMITS } from "../limits";
import { email, httpUrlOrEmpty, optionalText, queryBoolean, requiredText, searchQuery, uuid } from "./common";

// ---------- Requests ----------

const contactFields = {
  name: requiredText(LIMITS.expertName, "Name"),
  email: email.nullable(),
  phone: optionalText(LIMITS.phone, "Phone"),
  position: optionalText(LIMITS.positionTitle, "Position"),
  stakeholderOrgId: uuid.nullable(),
  country: countryCode.nullable(),
  gender: z.enum(GENDERS).nullable(),
  mayContact: z.boolean(),
  notes: optionalText(LIMITS.contactNotes, "Notes"),
};

/** 65. POST /api/contacts. `mayContact` defaults to true for contacts added by hand. */
export const createContactBody = z
  .object({
    name: contactFields.name,
    email: contactFields.email.default(null),
    phone: contactFields.phone.default(""),
    position: contactFields.position.default(""),
    stakeholderOrgId: contactFields.stakeholderOrgId.default(null),
    country: contactFields.country.default(null),
    gender: contactFields.gender.default(null),
    mayContact: contactFields.mayContact.default(true),
    notes: contactFields.notes.default(""),
  })
  .strict();
export type CreateContactBody = z.input<typeof createContactBody>;
export type CreateContactInput = z.output<typeof createContactBody>;

/** 67. PATCH /api/contacts/:id */
export const updateContactBody = z
  .object({
    name: contactFields.name.optional(),
    email: contactFields.email.optional(),
    phone: contactFields.phone.optional(),
    position: contactFields.position.optional(),
    stakeholderOrgId: contactFields.stakeholderOrgId.optional(),
    country: contactFields.country.optional(),
    gender: contactFields.gender.optional(),
    mayContact: contactFields.mayContact.optional(),
    notes: contactFields.notes.optional(),
  })
  .strict();
export type UpdateContactBody = z.infer<typeof updateContactBody>;

/** 64. GET /api/contacts */
export const contactListQuery = z
  .object({
    q: searchQuery.optional(),
    stakeholderOrgId: uuid.optional(),
    type: z.enum(STAKEHOLDER_TYPES).optional(),
    country: countryCode.optional(),
    mayContact: queryBoolean.optional(),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(LIMITS.contactsPageMax).default(LIMITS.contactsPageDefault),
  })
  .strict();
export type ContactListQuery = z.infer<typeof contactListQuery>;

const stakeholderOrgFields = {
  name: requiredText(LIMITS.stakeholderOrgName, "Name"),
  type: z.enum(STAKEHOLDER_TYPES),
  country: countryCode.nullable(),
  website: httpUrlOrEmpty(LIMITS.website),
  notes: optionalText(LIMITS.stakeholderOrgNotes, "Notes"),
};

/** 70. POST /api/stakeholder-orgs */
export const createStakeholderOrgBody = z
  .object({
    name: stakeholderOrgFields.name,
    type: stakeholderOrgFields.type.default("other"),
    country: stakeholderOrgFields.country.default(null),
    website: stakeholderOrgFields.website.default(""),
    notes: stakeholderOrgFields.notes.default(""),
  })
  .strict();
export type CreateStakeholderOrgBody = z.input<typeof createStakeholderOrgBody>;
export type CreateStakeholderOrgInput = z.output<typeof createStakeholderOrgBody>;

/** 71. PATCH /api/stakeholder-orgs/:id */
export const updateStakeholderOrgBody = z
  .object({
    name: stakeholderOrgFields.name.optional(),
    type: stakeholderOrgFields.type.optional(),
    country: stakeholderOrgFields.country.optional(),
    website: stakeholderOrgFields.website.optional(),
    notes: stakeholderOrgFields.notes.optional(),
  })
  .strict();
export type UpdateStakeholderOrgBody = z.infer<typeof updateStakeholderOrgBody>;

/** 69. GET /api/stakeholder-orgs */
export const stakeholderOrgListQuery = z
  .object({
    q: searchQuery.optional(),
    type: z.enum(STAKEHOLDER_TYPES).optional(),
    country: countryCode.optional(),
  })
  .strict();
export type StakeholderOrgListQuery = z.infer<typeof stakeholderOrgListQuery>;

// ---------- Responses ----------

export type StakeholderOrg = {
  id: string;
  name: string;
  type: StakeholderType;
  country: string | null;
  website: string;
  notes: string;
  contactCount: number;
  /** distinct events any of its contacts attended */
  eventsAttended: number;
  contributionCount: number;
  createdAt: string;
  updatedAt: string;
};

export type ContactSummary = {
  id: string;
  name: string;
  email: string | null;
  position: string;
  organisation: { id: string; name: string; type: StakeholderType } | null;
  country: string | null;
  gender: Gender | null;
  mayContact: boolean;
  source: ContactSource;
  eventsAttended: number;
  contributionCount: number;
  createdAt: string;
};

/** 64. GET /api/contacts */
export type ContactsPage = { items: ContactSummary[]; nextCursor: string | null };

export type ContributionItem = {
  id: string;
  role: ContributionRole;
  target:
    | { kind: "activity"; torId: string; title: string; activityCode: string; contractReference: string }
    | { kind: "deliverable"; deliverableId: string; title: string; contractReference: string };
};

export type ContactDetail = ContactSummary & {
  phone: string;
  notes: string;
  /** newest event first */
  participation: {
    registrationId: string;
    eventId: string;
    eventTitle: string;
    startDate: string;
    status: RegistrationStatus;
    attended: boolean;
  }[];
  contributions: ContributionItem[];
  updatedAt: string;
  /** delete = admin */
  can: { edit: boolean; delete: boolean };
};

/** The name a removed contact's registrations keep (ARCHITECTURE 18.5). */
export const REMOVED_PERSON_NAME = "Removed person";
